import hu from "./sales-country-copy/hu.json";
import bg from "./sales-country-copy/bg.json";
import sk from "./sales-country-copy/sk.json";
import sl from "./sales-country-copy/sl.json";
import { isSalesCountryLocale, type SalesCountryLocale } from "@/config/locale";
import source from "./sales-country-copy-source.json";
import translated from "./sales-country-copy.json";

/** Every translated UI field must have the same key as the source export. */
export type SalesCountryCopyKey = keyof typeof source;
export type SalesCountryCopy = Record<SalesCountryCopyKey, string>;
/**
 * A locale store may legitimately lag the source export while its reviewed copy is
 * prepared. Consumers must check the key (and category content checks the complete
 * shape) instead of assuming every locale has every key.
 */
export type SalesCountryCopyStore = Partial<Record<SalesCountryLocale, Partial<SalesCountryCopy>>>;

/** Missing locales remain explicit until their complete, reviewed writer output is integrated. */
export const salesCountryCopy: SalesCountryCopyStore = {
	...translated,
	hu,
	bg,
	sk,
	sl,
} satisfies SalesCountryCopyStore;
export const salesCountryCopyKeys = Object.keys(source) as SalesCountryCopyKey[];

export function readSalesCountryCopy(
	store: SalesCountryCopyStore,
	locale: SalesCountryLocale,
	key: SalesCountryCopyKey,
): string {
	const value = store[locale]?.[key];
	if (typeof value !== "string" || value.trim() === "") {
		throw new Error(`[locale/content] Missing supplemental copy: ${locale}:${key}`);
	}
	return value;
}

/** Whether reviewed supplemental copy has been integrated for this sales-country locale. */
export function hasSalesCountryCopy(locale: string | undefined): locale is SalesCountryLocale {
	return isSalesCountryLocale(locale) && Object.keys(salesCountryCopy[locale] ?? {}).length > 0;
}

/**
 * Legacy locales retain their authored sources. A sales-country locale without an
 * integrated store keeps the registry fallback chain and its authored sources; an
 * integrated store must be complete, so a missing key throws instead of mixing languages.
 */
export function getSalesCountryCopy(
	locale: string | undefined,
	key: SalesCountryCopyKey,
): string | undefined {
	if (!hasSalesCountryCopy(locale)) return undefined;
	return readSalesCountryCopy(salesCountryCopy, locale, key);
}
